import { translate } from "@kaltura/ds-react-utils";
import { ChannelData } from "../../types";
import {
    StyledCard,
    StyledThumbnail,
    StyledInfo,
    StyledLink,
    Separator,
    StyledActions,
    StyledMediaCount,
} from "./styled-components";
import { Button, Typography } from "@kaltura/ds-react-components";
import { Subscribe } from "@kaltura/mediaspace-shared-subscribe";
import { forwardRef, useContext, useEffect, useState } from "react";
import { ChannelsContext } from "../../../../ChannelsContext";
import { HtmlReactParser, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { SpanSrOnly, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

const Card = forwardRef<HTMLDivElement, ChannelData>((props, ref) => {
    const { id, name, members, subscribers, thumbnailUrl, channelUrl, isUserSubscribed, mediaCount } = props;

    const { subscribeUrl, unsubscribeUrl } = useContext(ChannelsContext);

    const [subscribersCount, setSubscribersCount] = useState(subscribers ?? 0);

    const updateSubscribers = (isSubscribed: boolean) => {
        setSubscribersCount((prev) => prev + (isSubscribed ? 1 : -1));
    };

    useEffect(() => {
        // reset subscribers count state when new data comes from the parent component
        if (subscribers !== undefined) {
            setSubscribersCount(subscribers);
        }
    }, [subscribers]);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleNavigateToChannel = () => {
        sendButtonAnalytics("Channel link", ButtonClickAnalyticsType.THUMBNAIL);
    };

    return (
        <StyledCard
            tabIndex={0}
            aria-labelledby={`sr-label-${id} sr-media-count-${id} sr-info-${id}`}
            role="gridcell"
            ref={ref}
        >
            <SpanSrOnly id={`sr-label-${id}`}>{translate("channel %1 card", [name])}</SpanSrOnly>
            <StyledThumbnail
                style={{ backgroundImage: `url("${thumbnailUrl}")` }}
                className={"kms-ds-channels-card-thumbnail"}
                href={channelUrl}
                onClick={handleNavigateToChannel}
                aria-label={translate("Go to channel %1", [name])}
            >
                {mediaCount !== undefined && (
                    <StyledMediaCount aria-hidden={true} id={`sr-media-count-${id}`}>
                        {translatePlural("%1 Media", "%1 Media", [mediaCount])}
                    </StyledMediaCount>
                )}
            </StyledThumbnail>
            <StyledLink href={channelUrl} onClick={handleNavigateToChannel} aria-describedby={`sr-link-label-${id}`}>
                <TruncateWithTooltip text={HtmlReactParser(name)}>
                    <Typography variant={"h4"} component={"h2"}>
                        {name}
                    </Typography>
                </TruncateWithTooltip>
            </StyledLink>
            <SpanSrOnly id={`sr-link-label-${id}`}>{translate("Go to channel %1", [name])}</SpanSrOnly>
            <StyledInfo id={`sr-info-${id}`}>
                {members !== undefined && translatePlural("%1 Member", "%1 Members", [members])}
                {members !== undefined && subscribers !== undefined && <Separator />}
                {subscribers !== undefined && translatePlural("%1 Subscriber", "%1 Subscribers", [subscribersCount])}
            </StyledInfo>
            <StyledActions>
                <Button
                    aria-label={translate("Go to channel %1", [name])}
                    href={channelUrl}
                    onClick={handleNavigateToChannel}
                >
                    {translate("Go to channel")}
                </Button>
                {subscribers !== undefined && isUserSubscribed !== undefined && (
                    <Subscribe
                        channelId={id}
                        subscribeUrl={subscribeUrl}
                        unsubscribeUrl={unsubscribeUrl}
                        subscribed={isUserSubscribed}
                        isBorderless={true}
                        onSubscribe={updateSubscribers}
                    />
                )}
            </StyledActions>
        </StyledCard>
    );
});

export default Card;
